import { ThreeEvent } from "@react-three/fiber";
import { RulerLineType, SingleLineType, WallType, zIndexEpsilon, isSingleLineType } from "../../types/floorplannerTypes";
import * as THREE from 'three';
import { Line } from "@react-three/drei";
import { calculateObjectPositionAndRotation } from "./calculateObjectPositionAndRotation";
import { getMiddleHandleOffset, singleLineHandleZ } from "./createMiddleDragHandle";
import { SELECTED_HANDLE_INTERACTION_Z, SELECTED_HANDLE_RENDER_ORDER } from "../../constants/canvas";

const calculateRotatedPosition = (position: THREE.Vector3, origin: THREE.Vector3, angle: number): THREE.Vector3 => {
    const cosTheta = Math.cos(angle);
    const sinTheta = Math.sin(angle);

    const x = cosTheta * (position.x - origin.x) - sinTheta * (position.y - origin.y) + origin.x;
    const y = sinTheta * (position.x - origin.x) + cosTheta * (position.y - origin.y) + origin.y;

    return new THREE.Vector3(x, y, position.z); // Ensure to preserve z position
};

const LeftRightArrow = (props: {
    wallWidth: number;
    position: [number, number, number];
    color: string;
}) => {
    const { wallWidth, position, color } = props;
    const noopRaycast = () => null;
    const z = 0.004;
    const strokeWidth = 1;
    const tipX = wallWidth * 0.36;
    const arrowHeadSize = Math.min(0.016, wallWidth * 0.16);
    const rightTip = new THREE.Vector3(tipX, 0, z);
    const leftTip = new THREE.Vector3(-tipX, 0, z);

    return (
        <group position={position}>
            <Line points={[rightTip, leftTip]} color={color} lineWidth={strokeWidth} raycast={noopRaycast} />
            <Line points={[rightTip, rightTip.clone().add(new THREE.Vector3(-arrowHeadSize, arrowHeadSize, 0))]} color={color} lineWidth={strokeWidth} raycast={noopRaycast} />
            <Line points={[rightTip, rightTip.clone().add(new THREE.Vector3(-arrowHeadSize, -arrowHeadSize, 0))]} color={color} lineWidth={strokeWidth} raycast={noopRaycast} />
            <Line points={[leftTip, leftTip.clone().add(new THREE.Vector3(arrowHeadSize, arrowHeadSize, 0))]} color={color} lineWidth={strokeWidth} raycast={noopRaycast} />
            <Line points={[leftTip, leftTip.clone().add(new THREE.Vector3(arrowHeadSize, -arrowHeadSize, 0))]} color={color} lineWidth={strokeWidth} raycast={noopRaycast} />
        </group>
    );
};

const HandleOutline = (props: {
    size: number;
    color: string;
}) => {
    const { size, color } = props;
    const noopRaycast = () => null;
    const half = size / 2;
    const z = 0.006;
    const topLeft = new THREE.Vector3(-half, half, z);
    const topRight = new THREE.Vector3(half, half, z);
    const bottomRight = new THREE.Vector3(half, -half, z);
    const bottomLeft = new THREE.Vector3(-half, -half, z);

    return (
        <group>
            <Line points={[topLeft, topRight]} color={color} lineWidth={1} raycast={noopRaycast} />
            <Line points={[topRight, bottomRight]} color={color} lineWidth={1} raycast={noopRaycast} />
            <Line points={[bottomRight, bottomLeft]} color={color} lineWidth={1} raycast={noopRaycast} />
            <Line points={[bottomLeft, topLeft]} color={color} lineWidth={1} raycast={noopRaycast} />
        </group>
    );
};

export const MiddleHHandle = (props: {
    wall: WallType | SingleLineType | RulerLineType;
    wallWidth: number;
    fillColor: string;
    color: string;
    onHandlePointerDown: (event: ThreeEvent<PointerEvent>, id: string) => void;
    onHandlePointerEnter: (event: ThreeEvent<PointerEvent>, id: string) => void;
    onHandlePointerLeave: (event: ThreeEvent<PointerEvent>, id: string) => void;
    centerPoint?: THREE.Vector2;
}) => {
    const {
        wall,
        wallWidth,
        fillColor,
        color,
        onHandlePointerDown,
        onHandlePointerEnter,
        onHandlePointerLeave,
        centerPoint,
    } = props;

    const { position, rotation } = calculateObjectPositionAndRotation(
        wall.start,
        wall.end
    );
    // Use the wallWidth prop directly - it already has length clamping applied by the caller
    const handleBaseSize = wallWidth;
    const wallLength = wall.start.distanceTo(wall.end);
    const handleOffset = getMiddleHandleOffset(wallLength, handleBaseSize);
    const zIndexOffset = isSingleLineType(wall) ? (wall.zIndex || 0) * zIndexEpsilon : 0;
    const handleZ = isSingleLineType(wall) ? singleLineHandleZ : SELECTED_HANDLE_INTERACTION_Z;
    const handleCenter = centerPoint
        ? new THREE.Vector3(centerPoint.x, centerPoint.y, 0)
        : position;

    return (
        <group
            position={
                calculateRotatedPosition(
                    handleCenter.clone().add(new THREE.Vector3(handleOffset, 0, handleZ + zIndexOffset)),
                    handleCenter,
                    rotation
                )}
            rotation={[0, 0, rotation]}
            renderOrder={SELECTED_HANDLE_RENDER_ORDER}
            onPointerDown={(event) => onHandlePointerDown(event, "middleH")}
            onPointerEnter={(event) => onHandlePointerEnter(event, "middleH")}
            onPointerLeave={(event) => onHandlePointerLeave(event, "middleH")}
        >
            <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                <planeGeometry args={[handleBaseSize, handleBaseSize]} />
                <meshBasicMaterial color={fillColor} />
            </mesh>
            <LeftRightArrow
                wallWidth={handleBaseSize}
                position={[0, 0, 0]}
                color={color}
            />
            <HandleOutline size={handleBaseSize} color={color} />
        </group>
    );
};
